(() => {
  document.documentElement.classList.remove('no-js');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const browseCleanup = new Map();
  const scrollBehavior = () => reducedMotion.matches ? 'auto' : 'smooth';
  const scrollWithin = (track, item, center = false) => {
    if (!track || !item) return;
    const row = track.getBoundingClientRect();
    const card = item.getBoundingClientRect();
    const rtl = getComputedStyle(track).direction === 'rtl';
    const delta = center ? card.left + card.width / 2 - row.left - row.width / 2
      : rtl ? card.right - row.right : card.left - row.left;
    track.scrollBy({ left: delta, behavior: scrollBehavior() });
  };

  const initBrowseRail = (root) => {
    if (browseCleanup.has(root)) return;
    const track = root.querySelector('[data-browse-track]');
    const items = [...(track?.querySelectorAll('[data-browse-item]') || [])];
    const controls = root.querySelector('[data-browse-controls]');
    if (!track || !controls || !items.length) return;
    const prev = root.querySelector('[data-browse-prev]');
    const next = root.querySelector('[data-browse-next]');
    const progress = root.querySelector('[data-browse-progress]');
    const count = root.querySelector('[data-browse-count]');
    const hint = root.querySelector('[data-browse-hint]');
    const controller = new AbortController();
    const { signal } = controller;
    let frame = 0;
    let rtl = false;
    const interact = () => root.classList.add('has-interacted');
    const relativeStart = item => {
      const row = track.getBoundingClientRect();
      const card = item.getBoundingClientRect();
      return rtl ? row.right - card.right : card.left - row.left;
    };
    const update = () => {
      frame = 0;
      rtl = getComputedStyle(track).direction === 'rtl';
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const position = Math.min(max, Math.max(0, rtl ? -track.scrollLeft : track.scrollLeft));
      const overflows = max > 3;
      root.classList.add('is-ready');
      root.classList.toggle('has-overflow', overflows);
      root.classList.toggle('can-scroll-prev', overflows && position > 3);
      root.classList.toggle('can-scroll-next', overflows && position < max - 3);
      controls.hidden = !overflows;
      prev.disabled = !overflows || position <= 3;
      next.disabled = !overflows || position >= max - 3;
      const ratio = max ? position / max : 0;
      const size = Math.max(18, Math.min(100, track.clientWidth / track.scrollWidth * 100));
      progress.style.width = `${size}%`;
      progress.style.left = `${(rtl ? 1 - ratio : ratio) * (100 - size)}%`;
      if (hint) hint.textContent = overflows && position >= max - 3 ? 'Browse back'
        : window.matchMedia('(max-width: 1023px), (pointer: coarse)').matches || navigator.maxTouchPoints > 0 ? 'Swipe to browse' : 'Browse collections';
      if (count) {
        let closest = 0;
        let distance = Infinity;
        items.forEach((item, i) => {
          const d = Math.abs(relativeStart(item));
          if (d < distance) { distance = d; closest = i; }
        });
        if (overflows && position >= max - 3) closest = items.length - 1;
        count.textContent = `${String(closest + 1).padStart(2, '0')} / ${String(items.length).padStart(2, '0')}`;
      }
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const move = direction => {
      interact();
      // Step to the next item, without changing selection or moving the page vertically.
      const target = direction > 0 ? items.find(item => relativeStart(item) > 4) || items.at(-1)
        : [...items].reverse().find(item => relativeStart(item) < -4) || items[0];
      scrollWithin(track, target);
    };
    prev.addEventListener('click', () => move(-1), { signal });
    next.addEventListener('click', () => move(1), { signal });
    track.addEventListener('scroll', schedule, { passive: true, signal });
    track.addEventListener('pointerdown', interact, { passive: true, signal });
    track.addEventListener('wheel', interact, { passive: true, signal });
    track.addEventListener('keydown', event => {
      if (track.getAttribute('role') === 'tablist' || event.target !== track) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      interact();
      if (event.key === 'Home' || event.key === 'End') scrollWithin(track, event.key === 'Home' ? items[0] : items.at(-1));
      else move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
    }, { signal });
    const resize = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;
    resize?.observe(track);
    items.forEach(item => resize?.observe(item));
    window.addEventListener('resize', schedule, { passive: true, signal });
    const visibility = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(entry => root.classList.toggle('is-inview', entry.isIntersecting));
    }, { threshold: .15 }) : null;
    visibility?.observe(root);
    const reveal = 'IntersectionObserver' in window && root.classList.contains('browse-rail--collections')
      ? new IntersectionObserver(entries => {
        entries.filter(entry => entry.isIntersecting).forEach((entry, index) => {
          if (!reducedMotion.matches) entry.target.animate(
            [{ opacity: .5, translate: '0 12px' }, { opacity: 1, translate: '0 0' }],
            { duration: 420, delay: index * 45, easing: 'cubic-bezier(.2,.7,.2,1)' }
          );
          reveal.unobserve(entry.target);
        });
      }, { threshold: .12 }) : null;
    items.forEach(item => reveal?.observe(item));
    update();
    browseCleanup.set(root, () => {
      controller.abort(); resize?.disconnect(); visibility?.disconnect(); reveal?.disconnect(); cancelAnimationFrame(frame);
      browseCleanup.delete(root);
    });
  };

  const initHero = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const slides = [...root.querySelectorAll('[data-hero-slide]')];
    const dots = [...root.querySelectorAll('[data-hero-dot]')];
    const prev = root.querySelector('[data-hero-prev]');
    const next = root.querySelector('[data-hero-next]');
    if (!slides.length) return;
    let index = 0;
    let timer;
    let paused = false;
    const delay = Number(root.dataset.autoplayDelay || 5000);
    const autoplay = root.dataset.autoplay !== 'false';

    const show = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.querySelectorAll('a,button,input,select,textarea').forEach(el => el.tabIndex = active ? 0 : -1);
      });
      dots.forEach((dot, i) => dot.setAttribute('aria-selected', i === index ? 'true' : 'false'));
    };
    const stop = () => { if (timer) clearInterval(timer); timer = undefined; };
    const start = () => {
      stop();
      if (!autoplay || paused || slides.length < 2) return;
      timer = setInterval(() => show(index + 1), delay);
    };
    const pause = () => { paused = true; stop(); };
    const resume = () => { paused = false; start(); };

    prev?.addEventListener('click', () => { show(index - 1); start(); });
    next?.addEventListener('click', () => { show(index + 1); start(); });
    dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); start(); }));
    root.addEventListener('mouseenter', pause);
    root.addEventListener('mouseleave', resume);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', (event) => { if (!root.contains(event.relatedTarget)) resume(); });
    show(0); start();
  };

  const initTabs = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const panels = [...root.querySelectorAll('[role="tabpanel"]')];
    const viewAll = root.querySelector('[data-tabs-view-all]');
    const track = root.querySelector('[role="tablist"]');
    const activate = (tab, focus = false) => {
      if (!tab) return;
      const changed = tab.getAttribute('aria-selected') !== 'true';
      const target = tab.getAttribute('aria-controls');
      tabs.forEach(t => {
        t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        t.tabIndex = t === tab ? 0 : -1;
      });
      panels.forEach(p => p.hidden = p.id !== target);
      if (viewAll && tab.dataset.collectionUrl) viewAll.href = tab.dataset.collectionUrl;
      if (focus) tab.focus({ preventScroll: true });
      scrollWithin(track, tab, true);
      root.querySelector('[data-browse-rail]')?.classList.add('has-interacted');
      const panel = panels.find(p => p.id === target);
      if (changed && !reducedMotion.matches) panel?.querySelectorAll('[data-product-card]').forEach((card, i) => {
        card.getAnimations().forEach(animation => animation.cancel());
        card.animate([{ opacity: .35, transform: 'translateY(10px)' }, { opacity: 1, transform: 'translateY(0)' }],
          { duration: 340, delay: Math.min(i, 3) * 45, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'backwards' });
      });
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const direction = (event.key === 'ArrowRight' ? 1 : -1) * (getComputedStyle(track).direction === 'rtl' ? -1 : 1);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + direction + tabs.length) % tabs.length;
        activate(tabs[next], true);
      });
    });
    root.addEventListener('shopify:block:select', event => {
      const tab = tabs.find(item => item.id.endsWith(`-${event.detail.blockId}`));
      if (tab) activate(tab);
    });
  };

  const initDrawer = (header) => {
    const openBtn = header?.querySelector('[data-drawer-open]');
    const drawer = document.querySelector('[data-mobile-drawer]');
    if (!openBtn || !drawer || openBtn.dataset.initialized === 'true') return;
    openBtn.dataset.initialized = 'true';

    const panel = drawer.querySelector('.mobile-drawer__panel');
    const rootMenu = drawer.querySelector('[data-mobile-menu-root]');
    const closeButtons = [...drawer.querySelectorAll('[data-drawer-close]')];
    const submenuOpeners = [...drawer.querySelectorAll('[data-submenu-open]')];
    const submenuClosers = [...drawer.querySelectorAll('[data-submenu-close]')];
    const submenus = [...drawer.querySelectorAll('[data-mobile-submenu]')];
    const mobileSearchInput = drawer.querySelector('[data-mobile-search-input]');
    const mobileSearchFocusButtons = [...drawer.querySelectorAll('[data-mobile-search-focus]')];
    let activeSubmenu = null;
    let lastSubmenuOpener = null;
    let menuState = 'closed';
    let settleTimer;
    let focusTimer;
    let restoreFocus = true;
    const mobileViewport = window.matchMedia('(max-width: 1023px)');
    const mainContent = document.getElementById('MainContent');
    let mainWasInert = false;
    drawer.dataset.menuMotion = 'true';
    drawer.dataset.menuState = menuState;
    const entranceItems = [
      drawer.querySelector('.mobile-drawer__brand'),
      drawer.querySelector('.mobile-drawer__search-form'),
      rootMenu?.querySelector('.mobile-drawer__section-label'),
      ...drawer.querySelectorAll('.mobile-drawer__nav-link')
    ].filter(Boolean);
    entranceItems.forEach((item, i) => {
      item.dataset.menuItem = '';
      item.style.setProperty('--menu-order', Math.min(i, 7));
      item.style.setProperty('--menu-reverse', Math.min(entranceItems.length - i - 1, 7));
    });
    submenus.forEach(submenu => {
      submenu.inert = true;
      submenu.querySelectorAll('.mobile-drawer__submenu-link,.mobile-drawer__submenu-grandchildren a').forEach((item, i) => {
        item.dataset.submenuItem = '';
        item.style.setProperty('--menu-order', Math.min(i, 6));
      });
    });

    const focusables = () => [...drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),[tabindex]:not([tabindex="-1"])')]
      .filter(el => el.offsetParent !== null && !el.closest('[aria-hidden="true"],[inert]'));

    const resetSubmenu = () => {
      activeSubmenu = null;
      submenus.forEach(sub => { sub.setAttribute('aria-hidden', 'true'); sub.inert = true; });
      rootMenu?.classList.remove('is-shifted');
      if (rootMenu) rootMenu.inert = false;
    };

    const finishClose = () => {
      clearTimeout(settleTimer);
      clearTimeout(focusTimer);
      menuState = 'closed';
      drawer.dataset.menuState = menuState;
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      openBtn.setAttribute('aria-expanded', 'false');
      if (mainContent) mainContent.inert = mainWasInert;
      resetSubmenu();
      if (restoreFocus && mobileViewport.matches) openBtn.focus({ preventScroll: true });
    };

    const open = () => {
      if (!mobileViewport.matches || menuState === 'open' || menuState === 'opening') return;
      clearTimeout(settleTimer);
      clearTimeout(focusTimer);
      if (menuState === 'closed') {
        resetSubmenu();
        mainWasInert = Boolean(mainContent?.inert);
      }
      if (mainContent) mainContent.inert = true;
      drawer.setAttribute('aria-hidden','false');
      document.body.classList.add('drawer-open');
      openBtn.setAttribute('aria-expanded','true');
      // Flush the off-screen pose once; CSS can then reverse cleanly on rapid taps.
      void panel?.offsetWidth;
      menuState = reducedMotion.matches ? 'open' : 'opening';
      drawer.dataset.menuState = menuState;
      focusTimer = window.setTimeout(() => (closeButtons[0] || panel)?.focus({ preventScroll: true }), reducedMotion.matches ? 0 : 120);
      if (!reducedMotion.matches) settleTimer = window.setTimeout(() => {
        menuState = 'open'; drawer.dataset.menuState = menuState;
      }, 620);
    };

    const close = (returnFocus = true) => {
      if (menuState === 'closed' || menuState === 'closing') return;
      clearTimeout(settleTimer);
      clearTimeout(focusTimer);
      restoreFocus = returnFocus;
      menuState = 'closing';
      drawer.dataset.menuState = menuState;
      if (reducedMotion.matches || !mobileViewport.matches) finishClose();
      else settleTimer = window.setTimeout(finishClose, 560);
    };

    const openSubmenu = (opener) => {
      const id = opener.getAttribute('data-submenu-open');
      const submenu = id ? drawer.querySelector(`#${CSS.escape(id)}`) : null;
      if (!submenu) return;
      submenus.forEach(sub => { sub.setAttribute('aria-hidden', sub === submenu ? 'false' : 'true'); sub.inert = sub !== submenu; });
      activeSubmenu = submenu;
      lastSubmenuOpener = opener;
      rootMenu?.classList.add('is-shifted');
      if (rootMenu) rootMenu.inert = true;
      clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => submenu.querySelector('[data-submenu-close]')?.focus({ preventScroll: true }), reducedMotion.matches ? 0 : 120);
    };

    const closeSubmenu = () => {
      if (!activeSubmenu) return;
      activeSubmenu.setAttribute('aria-hidden','true');
      activeSubmenu.inert = true;
      activeSubmenu = null;
      rootMenu?.classList.remove('is-shifted');
      if (rootMenu) rootMenu.inert = false;
      clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => lastSubmenuOpener?.focus({ preventScroll: true }), reducedMotion.matches ? 0 : 100);
    };

    openBtn.addEventListener('click', open);
    closeButtons.forEach(btn => btn.addEventListener('click', () => close()));
    submenuOpeners.forEach(btn => btn.addEventListener('click', () => openSubmenu(btn)));
    submenuClosers.forEach(btn => btn.addEventListener('click', closeSubmenu));
    mobileSearchFocusButtons.forEach(btn => btn.addEventListener('click', () => {
      resetSubmenu();
      clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => mobileSearchInput?.focus({ preventScroll: true }), 30);
    }));
    mobileViewport.addEventListener('change', () => {
      if (!mobileViewport.matches && menuState !== 'closed') { restoreFocus = false; finishClose(); }
    });
    reducedMotion.addEventListener('change', () => {
      if (!reducedMotion.matches) return;
      if (menuState === 'closing') finishClose();
      else if (menuState === 'opening') {
        clearTimeout(settleTimer); menuState = 'open'; drawer.dataset.menuState = menuState;
      }
    });
    header.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
      if (menuState !== 'closed') { restoreFocus = false; finishClose(); }
    });

    drawer.addEventListener('click', (event) => {
      if (event.target.closest('a[href]') && !event.target.closest('[data-submenu-open]')) {
        close(false);
      }
      if (event.target.closest('[data-search-open]')) {
        close(false);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (drawer.getAttribute('aria-hidden') === 'true') return;
      if (event.key === 'Escape') {
        event.preventDefault();
        if (menuState === 'closing') return;
        if (activeSubmenu) closeSubmenu(); else close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const initGallery = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const main = root.querySelector('.product-gallery__main img, .luxury-pdp__main-image');
    const current = root.querySelector('[data-gallery-current]');
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const surface = root.querySelector('[data-gallery-surface]');
    const error = root.querySelector('[data-gallery-error]');
    if (!main || !thumbs.length) return;
    let selected = Math.max(0, thumbs.findIndex(thumb => thumb.classList.contains('is-active')));
    let requested = selected;
    let requestId = 0;
    let touch = null;
    const show = async (index) => {
      requested = (index + thumbs.length) % thumbs.length;
      const target = requested;
      const version = ++requestId;
      if (target === selected) {
        root.classList.remove('is-loading'); root.setAttribute('aria-busy', 'false');
        if (error) error.hidden = true;
        return;
      }
      const btn = thumbs[target];
      if (error) error.hidden = true;
      root.classList.add('is-loading'); root.setAttribute('aria-busy', 'true');
      try {
        // Keep the current photo visible until its replacement has actually loaded.
        const image = new Image();
        await new Promise((resolve, reject) => {
          const timer = window.setTimeout(() => { image.onload = image.onerror = null; reject(new Error('Image timed out')); }, 12000);
          image.onload = () => { clearTimeout(timer); resolve(); };
          image.onerror = () => { clearTimeout(timer); reject(new Error('Image unavailable')); };
          image.sizes = main.sizes;
          image.srcset = btn.dataset.srcset || '';
          image.src = btn.dataset.src;
        });
        if (version !== requestId || !root.isConnected) return;
        main.getAnimations().forEach(animation => animation.cancel());
        main.srcset = btn.dataset.srcset || '';
        main.src = btn.dataset.src;
        main.alt = btn.dataset.alt || '';
        selected = target;
        thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === target);
          thumb.setAttribute('aria-pressed', i === target ? 'true' : 'false');
        });
        if (current) current.textContent = String(Number(btn.dataset.galleryIndex || target + 1)).padStart(2, '0');
        const row = btn.parentElement;
        if (row.scrollWidth > row.clientWidth) scrollWithin(row, btn, true);
        if (!reducedMotion.matches) main.animate([{ opacity: .4 }, { opacity: 1 }], { duration: 240, easing: 'ease-out' });
      } catch (_) {
        if (version !== requestId) return;
        requested = selected;
        if (error) { error.textContent = 'This image could not load. Please try again.'; error.hidden = false; }
      } finally {
        if (version === requestId) { root.classList.remove('is-loading'); root.setAttribute('aria-busy', 'false'); }
      }
    };
    thumbs.forEach((btn, i) => btn.addEventListener('click', () => show(i)));
    root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => show(requested - 1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => show(requested + 1));
    root.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const delta = (event.key === 'ArrowRight' ? 1 : -1) * (getComputedStyle(root).direction === 'rtl' ? -1 : 1);
      show(event.key === 'Home' ? 0 : event.key === 'End' ? thumbs.length - 1 : requested + delta);
    });
    surface?.addEventListener('pointerdown', event => {
      if (!event.isPrimary || event.pointerType === 'mouse') { touch = null; return; }
      touch = { x: event.clientX, y: event.clientY, id: event.pointerId };
      surface.setPointerCapture?.(event.pointerId);
    }, { passive: true });
    surface?.addEventListener('pointercancel', () => { touch = null; }, { passive: true });
    surface?.addEventListener('pointerup', event => {
      if (!touch || touch.id !== event.pointerId) return;
      const dx = event.clientX - touch.x;
      const dy = event.clientY - touch.y;
      touch = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
      const direction = (dx < 0 ? 1 : -1) * (getComputedStyle(root).direction === 'rtl' ? -1 : 1);
      show(requested + direction);
    }, { passive: true });
  };

  const initLuxuryPdp = (root) => {
    if (!root || root.dataset.pdpInitialized === 'true') return;
    root.dataset.pdpInitialized = 'true';
    const form = root.querySelector('.luxury-pdp__form');
    const minus = root.querySelector('[data-quantity-minus]');
    const plus = root.querySelector('[data-quantity-plus]');
    const quantity = root.querySelector('input[name="quantity"]');
    const selector = root.querySelector('[data-variant-selector]');
    const variantPicker = root.querySelector('[data-variant-picker]');
    const variantChoices = Array.from(root.querySelectorAll('[data-variant-choice]'));
    const price = root.querySelector('[data-pdp-price]');
    const compare = root.querySelector('[data-pdp-compare]');
    const add = root.querySelector('[data-main-add]');
    const buyNow = root.querySelector('[data-buy-now]');
    const sticky = document.querySelector('[data-pdp-mobile-sticky]');
    const stickyPrice = sticky?.querySelector('[data-sticky-price]');
    const stickyBuyNow = sticky?.querySelector('[data-sticky-buy-now]');
    const stickyAdd = sticky?.querySelector('[data-sticky-add]');

    const setQty = (delta) => {
      if (!quantity) return;
      const min = Number(quantity.min || 1);
      quantity.value = Math.max(min, Number(quantity.value || min) + delta);
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    };
    minus?.addEventListener('click', () => setQty(-1));
    plus?.addEventListener('click', () => setQty(1));

    const replaceMoney = (target, value) => {
      if (!target) return;
      // Fresh nodes discard a converter's cached amount for the previous variant.
      const money = document.createElement('span');
      money.className = 'money';
      money.textContent = value;
      target.replaceChildren(...(value ? [money] : []));
    };
    const syncVariant = (updatePrices = true) => {
      if (!selector) return;
      const option = selector.options[selector.selectedIndex];
      const available = option?.dataset.available === 'true';
      const newPrice = option?.dataset.price || '';
      const newCompare = option?.dataset.compare || '';
      if (option?.dataset.mediaId) {
        const thumb = root.querySelector(`[data-gallery-thumb][data-media-id="${CSS.escape(option.dataset.mediaId)}"]`);
        if (thumb && !thumb.classList.contains('is-active')) thumb.click();
      }
      if (updatePrices) {
        replaceMoney(price, newPrice);
        replaceMoney(stickyPrice, newPrice);
        replaceMoney(compare, newCompare);
        compare?.classList.toggle('is-hidden', !newCompare);
      }
      if (add) {
        add.disabled = !available;
        const label = add.querySelector('[data-add-label]');
        if (label) label.textContent = available ? 'Add to bag' : 'Sold out';
      }
      if (buyNow) {
        buyNow.disabled = !available;
        buyNow.textContent = available ? 'Buy now' : 'Sold out';
      }
      if (stickyBuyNow) {
        stickyBuyNow.disabled = !available;
        stickyBuyNow.textContent = available ? 'Buy now' : 'Sold out';
      }
    };
    const syncVariantPicker = () => {
      if (!selector || !variantChoices.length) return;
      variantChoices.forEach((choice) => {
        const selected = choice.value === selector.value;
        choice.checked = selected;
        const label = choice.closest('.luxury-pdp__variant-choice');
        label?.classList.toggle('is-selected', selected);
      });
    };

    variantPicker?.addEventListener('change', (event) => {
      const choice = event.target.closest('[data-variant-choice]');
      if (!choice || choice.disabled || !selector) return;
      selector.value = choice.value;
      selector.dispatchEvent(new Event('change', { bubbles: true }));
    });

    selector?.addEventListener('change', () => {
      syncVariantPicker();
      syncVariant();
    });
    stickyAdd?.addEventListener('click', () => {
      if (!form || stickyAdd.disabled) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit(add || undefined);
      else add?.click();
    });

    // Buy Now (desktop and mobile sticky) must go straight to checkout, not
    // add the item and sit on the product page. A plain form submit with
    // name="checkout" has no special meaning to Shopify's /cart/add endpoint —
    // it just adds to cart like the regular Add to bag button, which is why
    // Buy Now was behaving like Add to cart on both phone and desktop. Instead
    // we add to cart via AJAX and then navigate straight to /checkout.
    const checkoutNow = async (event) => {
      event?.preventDefault();
      const button = event?.currentTarget;
      if (!button || button.disabled) return;
      const selected = selector ? selector.options[selector.selectedIndex] : null;
      if (selected && selected.dataset.available !== 'true') return;
      const variantId = selector?.value || root.querySelector('input[name="id"]')?.value;
      const qty = Math.max(1, Number(quantity?.value || 1));
      if (!variantId) return;

      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Redirecting…';
      try {
        const response = await fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty }] })
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add this item.');
        }
        window.location.assign('/checkout');
        return;
      } catch (error) {
        // Fall back to a plain cart permalink so checkout still works even if
        // the AJAX call failed (offline, app interference, etc.).
        window.location.assign(`/cart/${variantId}:${qty}`);
        return;
      } finally {
        // Only reached if navigation above somehow didn't happen.
        button.disabled = false;
        button.textContent = originalText;
      }
    };

    buyNow?.addEventListener('click', checkoutNow);
    stickyBuyNow?.addEventListener('click', checkoutNow);

    // Keep the visible segmented picker aligned with Shopify's real variant select.
    syncVariantPicker();
    // Keep any prices already converted by an app during initial page loading.
    syncVariant(false);
  };

  const initLocalization = (form) => {
    if (!form || form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';
    form.querySelectorAll('select').forEach(select => select.addEventListener('change', () => form.submit()));
  };

  const initConfidenceVideo = (video) => {
    if (!video || video.dataset.initialized === 'true') return;
    video.dataset.initialized = 'true';
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    const play = () => {
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    };
    play();
    document.addEventListener('visibilitychange', () => { if (!document.hidden && video.paused) play(); });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.('[data-hero-carousel]').forEach(initHero);
    scope.querySelectorAll?.('[data-product-tabs]').forEach(initTabs);
    scope.querySelectorAll?.('[data-browse-rail]').forEach(initBrowseRail);
    scope.querySelectorAll?.('[data-site-header]').forEach(initDrawer);
    scope.querySelectorAll?.('[data-product-gallery]').forEach(initGallery);
    scope.querySelectorAll?.('[data-luxury-pdp]').forEach(initLuxuryPdp);
    scope.querySelectorAll?.('.localization-form').forEach(initLocalization);
    scope.querySelectorAll?.('.confidence-story__video').forEach(initConfidenceVideo);
  };
  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    browseCleanup.forEach((cleanup, root) => { if (event.target.contains(root)) cleanup(); });
  });
})();

/* True Essence premium commerce interactions: AJAX add-to-cart, scent-flight animation,
   perfume-bottle cart feedback, and question-led newsletter discount popup. */
(() => {
  const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const storage = {
    get(key) { try { return window.localStorage.getItem(key); } catch (_) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch (_) {} },
    remove(key) { try { window.localStorage.removeItem(key); } catch (_) {} }
  };

  const cartTarget = () => [...document.querySelectorAll('[data-cart-target]')]
    .find(target => target.getClientRects().length > 0 && !target.closest('[aria-hidden="true"]'));

  const updateCartCount = async () => {
    try {
      const response = await fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
      document.querySelectorAll('[data-cart-target]').forEach(target => {
        target.setAttribute('aria-label', `Cart, ${cart.item_count} items`);
      });
    } catch (_) {}
  };

  // Observe completed app cart requests (including PageFly) without replacing
  // fetch/XMLHttpRequest or intercepting third-party purchase forms.
  let appCartRefreshTimer;
  if ('PerformanceObserver' in window) {
    try {
      const cartRequests = new PerformanceObserver((list) => {
        const changed = list.getEntries().some(entry => {
          const url = new URL(entry.name, location.href);
          return url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js|\.json)?\/?$/.test(url.pathname);
        });
        if (!changed) return;
        clearTimeout(appCartRefreshTimer);
        appCartRefreshTimer = setTimeout(updateCartCount, 120);
      });
      cartRequests.observe({ entryTypes: ['resource'] });
    } catch (_) {}
  }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) updateCartCount();
  });

  const pulseCart = () => {
    const target = cartTarget();
    if (!target) return;
    target.classList.remove('is-receiving');
    void target.offsetWidth;
    target.classList.add('is-receiving');
    window.setTimeout(() => target.classList.remove('is-receiving'), 760);
  };

  const flyProductToCart = (source) => {
    const target = cartTarget();
    if (!source || !target || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      pulseCart();
      return;
    }
    const image = source.tagName === 'IMG' ? source : source.querySelector('img');
    if (!image) { pulseCart(); return; }
    const start = image.getBoundingClientRect();
    const end = target.getBoundingClientRect();
    const size = Math.max(46, Math.min(68, start.width * .28));
    const flyer = document.createElement('div');
    flyer.className = 'te-cart-flight';
    flyer.style.width = `${size}px`;
    flyer.style.height = `${size}px`;
    flyer.style.left = `${start.left + start.width / 2 - size / 2}px`;
    flyer.style.top = `${start.top + start.height / 2 - size / 2}px`;
    const clone = image.cloneNode(true);
    clone.removeAttribute('srcset');
    clone.removeAttribute('sizes');
    flyer.appendChild(clone);
    document.body.appendChild(flyer);

    const dx = end.left + end.width / 2 - (start.left + start.width / 2);
    const dy = end.top + end.height / 2 - (start.top + start.height / 2);
    const animation = flyer.animate([
      { transform: 'translate3d(0,0,0) scale(1)', opacity: 1 },
      { transform: `translate3d(${dx * .55}px,${dy * .45 - 42}px,0) scale(.72)`, opacity: .94, offset: .58 },
      { transform: `translate3d(${dx}px,${dy}px,0) scale(.2)`, opacity: .08 }
    ], { duration: 760, easing: 'cubic-bezier(.18,.72,.22,1)' });
    animation.onfinish = () => { flyer.remove(); pulseCart(); };
  };

  const toast = (title, message = 'Your fragrance has been added to the cart.') => {
    document.querySelector('.te-cart-toast')?.remove();
    const el = document.createElement('div');
    el.className = 'te-cart-toast';
    el.setAttribute('role', 'status');
    el.innerHTML = `
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9.5 2.5h5v3h-5z"/><path d="M10.5 5.5v2.1L7.8 10.2a2.1 2.1 0 0 0-.65 1.52v7.78h9.7v-7.78a2.1 2.1 0 0 0-.65-1.52l-2.7-2.6V5.5"/><path d="M9.35 13h5.3v3.6h-5.3z"/></svg>
      <div><strong>${title}</strong><span>${message}</span></div>`;
    document.body.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    window.setTimeout(() => {
      el.classList.remove('is-visible');
      window.setTimeout(() => el.remove(), 280);
    }, 3200);
  };

  const setButtonState = (button, state) => {
    if (!button) return;
    const label = button.querySelector('[data-add-label]') || button.querySelector('span');
    if (!button.dataset.defaultLabel && label) button.dataset.defaultLabel = label.textContent.trim();
    if (state === 'loading') {
      button.disabled = true;
      if (label) label.textContent = 'Adding…';
    } else if (state === 'added') {
      if (label) label.textContent = 'Added';
    } else {
      button.disabled = false;
      if (label && button.dataset.defaultLabel) label.textContent = button.dataset.defaultLabel;
    }
  };

  const handleAjaxAdd = async (event) => {
    const form = event.target.closest('.luxury-pdp__form, .product-card__quick-form');
    if (!form) return;
    const submitter = event.submitter;
    if (submitter && !submitter.matches('[data-main-add],[data-quick-add]')) return;
    event.preventDefault();

    const button = submitter || form.querySelector('[data-main-add],[data-quick-add]');
    if (!button || button.disabled) return;
    const card = form.closest('[data-product-card]');
    const pdp = form.closest('[data-luxury-pdp]');
    const source = card?.querySelector('[data-product-card-media]') || pdp?.querySelector('.luxury-pdp__media-arch');
    const productName = card?.querySelector('.product-card__title')?.textContent?.trim() || pdp?.querySelector('h1')?.textContent?.trim() || 'True Essence';

    card?.classList.add('is-adding');
    form.classList.add('is-adding');
    setButtonState(button, 'loading');

    try {
      const response = await fetch(`${routeRoot()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || 'Could not add this item.');

      flyProductToCart(source);
      await updateCartCount();
      setButtonState(button, 'added');
      card?.classList.add('is-added');
      toast(productName);

      window.setTimeout(() => {
        card?.classList.remove('is-added');
        setButtonState(button, 'default');
      }, 1600);
    } catch (error) {
      setButtonState(button, 'default');
      toast('Please try again', error.message || 'Could not add this item.');
    } finally {
      card?.classList.remove('is-adding');
      form.classList.remove('is-adding');
    }
  };

  const initDiscountPopup = (popup) => {
    if (!popup || popup.dataset.popupInitialized === 'true') return;
    popup.dataset.popupInitialized = 'true';
    const form = popup.closest('form');
    const closeButtons = [...popup.querySelectorAll('[data-discount-close]')];
    const openButtons = [...(form?.querySelectorAll('[data-discount-open]') || [])];
    const audienceButtons = [...popup.querySelectorAll('[data-discount-audience]')];
    const back = popup.querySelector('[data-discount-back]');
    const audienceStep = popup.querySelector('[data-discount-step="audience"]');
    const emailStep = popup.querySelector('[data-discount-step="email"]');
    const tags = popup.querySelector('[data-discount-tags]');
    const selection = popup.querySelector('[data-discount-selection]');
    const email = popup.querySelector('input[type="email"]');
    const code = (popup.dataset.discountCode || '').trim();
    const delay = Number(popup.dataset.delay || 2000);
    const repeatDays = Number(popup.dataset.repeatDays || 7);
    const version = popup.dataset.popupVersion || '6';
    const keyPrefix = `teDiscountPopupV${version}`;
    const dismissedKey = `${keyPrefix}DismissedAt`;
    const subscribedKey = `${keyPrefix}Subscribed`;
    const awaitingKey = `${keyPrefix}Awaiting`;
    const audienceKey = `${keyPrefix}Audience`;
    const dismissedAt = Number(storage.get(dismissedKey) || 0);
    const subscribed = storage.get(subscribedKey) === '1';
    const awaiting = storage.get(awaitingKey) === '1';
    const postedSuccess = popup.dataset.success === 'true';
    const hasErrors = popup.dataset.errors === 'true';
    const designMode = Boolean(window.Shopify && window.Shopify.designMode);

    const updateReopen = () => {
      openButtons.forEach((button) => {
        button.hidden = subscribed || popup.classList.contains('is-open');
      });
    };

    const applyShopifyDiscount = () => {
      storage.set(subscribedKey, '1');
      storage.remove(awaitingKey);
      updateReopen();
      if (!code) return;
      const redirect = window.location.pathname + window.location.search;
      window.location.replace(`${routeRoot()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`);
    };

    const postedParams = new URLSearchParams(window.location.search);
    const postedFlag = postedParams.get('customer_posted') === 'true' || postedParams.get('contact_posted') === 'true';
    if (awaiting && (postedSuccess || postedFlag)) {
      applyShopifyDiscount();
      return;
    }

    const show = () => {
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.body.classList.add('discount-popup-open');
      updateReopen();
      window.setTimeout(() => popup.querySelector('[data-discount-audience],input[type="email"]')?.focus(), 80);
    };
    const hide = (remember = true) => {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('discount-popup-open');
      if (remember) storage.set(dismissedKey, String(Date.now()));
      updateReopen();
    };
    const switchStep = (step) => {
      const emailActive = step === 'email';
      audienceStep?.classList.toggle('is-active', !emailActive);
      audienceStep?.setAttribute('aria-hidden', emailActive ? 'true' : 'false');
      emailStep?.classList.toggle('is-active', emailActive);
      emailStep?.setAttribute('aria-hidden', emailActive ? 'false' : 'true');
      if (emailActive) window.setTimeout(() => email?.focus(), 80);
    };

    audienceButtons.forEach((button) => button.addEventListener('click', () => {
      const value = button.dataset.discountAudience || 'Both';
      if (tags) tags.value = `newsletter,True Essence Popup,${value}`;
      if (selection) selection.textContent = value;
      storage.set(audienceKey, value);
      switchStep('email');
    }));
    back?.addEventListener('click', () => switchStep('audience'));
    closeButtons.forEach((button) => button.addEventListener('click', () => hide(true)));
    openButtons.forEach((button) => button.addEventListener('click', show));
    popup.addEventListener('keydown', (event) => { if (event.key === 'Escape') hide(true); });
    form?.addEventListener('submit', () => storage.set(awaitingKey, '1'));

    const recentlyDismissed = dismissedAt && Date.now() - dismissedAt < repeatDays * 86400000;
    if (hasErrors) {
      const savedAudience = storage.get(audienceKey) || 'Both';
      if (selection) selection.textContent = savedAudience;
      if (tags) tags.value = `newsletter,True Essence Popup,${savedAudience}`;
      window.setTimeout(show, 60);
    } else if (designMode) {
      window.setTimeout(show, 300);
    } else if (!subscribed && !recentlyDismissed && !postedSuccess && !document.querySelector('[data-luxury-pdp]')) {
      window.setTimeout(show, Math.max(0, delay));
    } else {
      updateReopen();
    }
  };

  const initPremiumCommerce = (scope = document) => {
    scope.querySelectorAll?.('[data-discount-popup]').forEach(initDiscountPopup);
  };

  document.addEventListener('submit', handleAjaxAdd);
  document.addEventListener('DOMContentLoaded', () => initPremiumCommerce());
  document.addEventListener('shopify:section:load', (event) => initPremiumCommerce(event.target));
})();

/* ============================================================
   Predictive search overlay — live results as you type, using
   Shopify's native /search/suggest.json endpoint. Falls back to
   a normal full-page search on submit / Enter.
   ============================================================ */
(() => {
  const overlay = document.querySelector('[data-search-overlay]');
  if (!overlay) return;

  const input = overlay.querySelector('[data-search-input]');
  const resultsEl = overlay.querySelector('[data-search-results]');
  const form = overlay.querySelector('[data-search-form]');
  let debounceTimer;
  let lastQuery = '';

  const escapeHtml = (str) => String(str || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));

  const open = () => {
    overlay.setAttribute('data-open', 'true');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    window.setTimeout(() => input && input.focus(), 60);
  };

  const close = () => {
    overlay.setAttribute('data-open', 'false');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  const renderEmpty = (message) => {
    resultsEl.innerHTML = `<p class="search-overlay__empty">${escapeHtml(message)}</p>`;
  };

  const renderResults = (products, term) => {
    if (!products.length) {
      renderEmpty(`No results for "${term}". Press enter to search anyway.`);
      return;
    }
    const items = products.map((p) => `
      <a class="search-overlay__result" href="${p.url}">
        ${p.image ? `<img src="${p.image}" alt="" loading="lazy">` : ''}
        <span class="search-overlay__result-title">${escapeHtml(p.title)}</span>
      </a>
    `).join('');
    resultsEl.innerHTML = `
      <div class="search-overlay__result-list">${items}</div>
      <a class="search-overlay__viewall" href="${window.Shopify ? '' : ''}${form.action}?q=${encodeURIComponent(term)}">View all results for "${escapeHtml(term)}"</a>
    `;
  };

  const runSearch = (term) => {
    if (!term) { renderEmpty('Start typing to search fragrances.'); return; }
    if (term === lastQuery) return;
    lastQuery = term;
    fetch(`/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,vendor,tag`)
      .then((r) => r.json())
      .then((data) => {
        const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        renderResults(products, term);
      })
      .catch(() => renderEmpty('Search is unavailable right now — press enter to try the full search page.'));
  };

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-open]')) { e.preventDefault(); open(); if (input) runSearch(input.value.trim()); }
    if (e.target.closest('[data-search-close]')) { e.preventDefault(); close(); }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.getAttribute('data-open') === 'true') close();
  });

  if (input) {
    input.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      const term = input.value.trim();
      debounceTimer = setTimeout(() => runSearch(term), 200);
    });
  }

  renderEmpty('Start typing to search fragrances.');
})();


/* True Essence cart — instant AJAX quantity/removal updates. */
(() => {
  const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const updateHeaderCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; });
    document.querySelectorAll('[data-cart-target]').forEach((el) => {
      el.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
    });
  };

  const replaceCartSection = (currentCart, html) => {
    if (!html) return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const nextCart = doc.querySelector('[data-cart-page]');
    if (!nextCart) return false;
    currentCart.replaceWith(nextCart);
    initCart(nextCart.ownerDocument === document ? nextCart : document.querySelector('[data-cart-page]'));
    return true;
  };

  const setBusy = (cart, busy) => {
    cart.classList.toggle('is-updating', busy);
    cart.setAttribute('aria-busy', busy ? 'true' : 'false');
    cart.querySelectorAll('[data-cart-qty-minus],[data-cart-qty-plus],[data-cart-qty-input],.te-cart-item__remove').forEach((el) => {
      if ('disabled' in el) el.disabled = busy;
      el.setAttribute('aria-disabled', busy ? 'true' : 'false');
    });
  };

  const changeLine = async (cart, line, quantity) => {
    if (!cart || cart.dataset.cartUpdating === 'true') return;
    const sectionId = cart.dataset.sectionId;
    if (!sectionId || !line) return;

    cart.dataset.cartUpdating = 'true';
    setBusy(cart, true);

    try {
      const response = await fetch(`${routeRoot()}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          line: Number(line),
          quantity: Math.max(0, Number(quantity) || 0),
          sections: sectionId,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
      const data = await response.json();
      updateHeaderCartCount(data.item_count || 0);

      const live = cart.querySelector('[data-cart-live]');
      if (live) live.textContent = quantity > 0 ? 'Cart updated.' : 'Item removed from cart.';

      if (!replaceCartSection(cart, data.sections && data.sections[sectionId])) {
        window.location.reload();
      }
    } catch (error) {
      console.error('True Essence cart update error:', error);
      setBusy(cart, false);
      cart.dataset.cartUpdating = 'false';
      window.location.reload();
    }
  };

  const initCart = (cart) => {
    if (!cart || cart.dataset.ajaxCartReady === 'true') return;
    cart.dataset.ajaxCartReady = 'true';

    cart.addEventListener('click', (event) => {
      const qtyButton = event.target.closest('[data-cart-qty-minus],[data-cart-qty-plus]');
      if (qtyButton && cart.contains(qtyButton)) {
        event.preventDefault();
        const item = qtyButton.closest('[data-cart-line]');
        const input = item?.querySelector('[data-cart-qty-input]');
        if (!item || !input) return;
        const current = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
        const next = qtyButton.hasAttribute('data-cart-qty-plus') ? current + 1 : Math.max(0, current - 1);
        input.value = String(next);
        changeLine(cart, item.dataset.cartLine, next);
        return;
      }

      const remove = event.target.closest('.te-cart-item__remove');
      if (remove && cart.contains(remove)) {
        event.preventDefault();
        const item = remove.closest('[data-cart-line]');
        if (item) changeLine(cart, item.dataset.cartLine, 0);
      }
    });

    cart.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-qty-input]');
      if (!input || !cart.contains(input)) return;
      const item = input.closest('[data-cart-line]');
      if (!item) return;
      const quantity = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
      input.value = String(quantity);
      changeLine(cart, item.dataset.cartLine, quantity);
    });
  };

  document.addEventListener('DOMContentLoaded', () => initCart(document.querySelector('[data-cart-page]')));
  document.addEventListener('shopify:section:load', (event) => initCart(event.target.querySelector?.('[data-cart-page]') || (event.target.matches?.('[data-cart-page]') ? event.target : null)));
})();

